import AcceptCheckbox from './AcceptCheckbox';
import BulletItem from './BulletItem';
import Divider from './Divider';
import TermsCard from './TermsCard';

const MediPayTermsSection = ({ isAdmin, isCompleted, mediPayAccepted, setMediPayAccepted }) => {
  return (
    <TermsCard
      title="MediPay / Medbill"
      subtitle="Processing participation, onboarding expectations, fees, and enforcement:">
      <ul className="flex flex-col gap-2">
        <BulletItem>
          <span className="font-600">Processing requirement.</span> If bundled or no-cost services
          (including Myriad Health access) are accepted, all credit card transactions must be
          processed through MediPay unless otherwise approved in writing. Standard minimums and
          service fees apply.
        </BulletItem>
        <BulletItem>
          <span className="font-600">Pricing structure.</span> The pricing structure - including
          processing fees and MediPay service fees - follows the same structural model used in
          standalone and integrated healthcare-exclusive processing. Bundled access does not create
          outside billing or additional pricing beyond what is reflected in your Savings Analysis.
        </BulletItem>
        <BulletItem>
          <span className="font-600">Minimums + ancillary fees.</span> Where applicable, ancillary
          service fees reflected in your Savings Analysis auto-apply after 90 days whether or not
          processing occurs, unless covered by a separate written SaaS arrangement. Standard
          processing minimums apply beginning in the first full month any traditional processing
          agreement is enacted (MediPay notwithstanding).
        </BulletItem>
        <BulletItem>
          <span className="font-600">Activation expectation.</span> Live processing is generally
          expected within 30 days of signature and does not require full completion of EHR
          onboarding. Ninety (90) days is the maximum acceptable timeframe for onboarding and/or
          processing activation.
        </BulletItem>
        <BulletItem>
          <span className="font-600">Onboarding participation.</span> Active participation in the
          onboarding program is required. This includes custom build configurations, integrations,
          live training sessions, healthcare program education, implementation support, and related
          onboarding services. These services are subsidized as part of the MediPay Loyalty Program
          and are waived when participation obligations are honored.
        </BulletItem>
        <BulletItem>
          <span className="font-600">Loyalty Program (billing structure).</span> You will incur two
          debits per month for MediPay processing service fees. These represent your quoted
          processing service fees (Savings Analysis) and will appear as &quot;Merchant
          Processing&quot; and &quot;MediPay Processing&quot; (descriptors may vary slightly
          depending on your backend processor and your bank&apos;s rendering of transaction
          descriptions). Together, these debits reflect the effective rate quoted in your Savings
          Analysis. This structure is designed so charges may decrease over time rather than
          continually increase, as commonly occurs under traditional retail processing models.
        </BulletItem>
        <BulletItem>
          <span className="font-600">Subsidized implementation investment.</span> MediPay invests
          significantly in establishing your healthcare-exclusive payment environment and supporting
          your continued partnership. As a Good Faith Courtesy, these setup and implementation costs
          are waived to eliminate upfront expenses for your practice. These costs may include
          account setup, healthcare account approval, system implementation, integrations, training
          resources, encryption configuration, equipment provisioning, partner onboarding, data
          migration, and related implementation services. The total setup investment may be up to{' '}
          <span className="font-600">$3,750</span>, depending on the services required for your
          account. These services are subsidized through the MediPay Loyalty Program and remain
          waived when participation obligations are honored. If onboarding or processing
          participation obligations are not honored outside the protections of the Good Faith
          Guarantee, MediPay may recover a{' '}
          <span className="font-600">
            prorated portion of the subsidized implementation investment
          </span>
          , as described in the Service Order Terms.
        </BulletItem>
        <BulletItem>
          <span className="font-600">MediPay Guarantees &amp; Additional Services.</span> MediPay
          does not charge a cancellation fee. Substantial onboarding services and other services may
          be subsidized in good faith, including live trainings, custom configurations,
          integrations, implementation support, and in some cases assistance covering third-party
          prior processor or software termination fees.
          {'\n'}If onboarding or processing participation obligations are not honored outside the
          protections of the Good Faith Guarantee, reimbursement of certain waived or subsidized
          implementation costs may apply as described in the Service Order Terms.
          {'\n'}MediPay must be given a reasonable opportunity to address competitive offers or
          performance concerns prior to cancellation.
        </BulletItem>
        <BulletItem>
          <span className="font-600">Backend processor + Healthcare Rider.</span> Your backend
          processor (Elavon or Payroc, depending on setup) maintains separate processing terms. As a
          healthcare-exclusive processor, MediPay adds a Healthcare Rider to your processing
          agreement to enable pre-authorized healthcare transactions, protect cards-on-file, enhance
          chargeback protections, and support bundled healthcare-exclusive pricing structures.
        </BulletItem>
        <BulletItem>
          <span className="font-600">90-day non-activation.</span> If processing is not live within
          90 days of acceptance of the bundled offering, Myriad may suspend or disable bundled
          services, including Myriad Health access, as permitted under applicable agreements and
          law.
        </BulletItem>
      </ul>
      <Divider />
      <div className="flex flex-col gap-2 rounded-xl p-4">
        <p className="text-sm font-600">Need ongoing billing support?</p>
        <p className="text-sm text-neutral-600">
          If you are not a MedBill client and would like additional onboarding and ongoing
          assistance with patient load, insurance billing, coding, credentialing, and related
          services, request a <span className="font-600">no-obligation consultation</span> for
          Built-in-Biller services{' '}
          <a
            href="https://myriadsystems.ai/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="font-400 italic text-primary-600 !underline hover:text-neutral-500">
            here
          </a>
          .
        </p>
      </div>
      <Divider />
      <AcceptCheckbox
        id="medipay-accept"
        label={
          <>
            I have read, understand, and agree to the MediPay terms above and in attached Terms as
            they relate to bundled Myriad Health participation and processing requirements.
          </>
        }
        isChecked={isCompleted || mediPayAccepted}
        onChange={() => setMediPayAccepted(!mediPayAccepted)}
        disabled={isCompleted || isAdmin}
        tooltip={
          isAdmin && !isCompleted
            ? 'This step must be completed by a user. Admins cannot acknowledge it.'
            : null
        }
      />
    </TermsCard>
  );
};

export default MediPayTermsSection;
